import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { classMap } from 'lit/directives/class-map.js'
import { consume } from '@lit/context'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, ProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { partsStyle } from 'components/discovery/product/tile-parts/tile-parts.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProductTag } from 'components/discovery/pdp/typing/pdp-product.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { tileTagsStyle } from './tile-tags.style.js'

export const tileTagsDataSources = ['tile', 'pdp-product'] as const
export type TileTagsDataSource = typeof tileTagsDataSources[number]

export const tileTagsHiddenFields = ['badges', 'pictures'] as const

type TileTag = { name: string, badgeType: string | null, picture: string | null }

const uploadsPicture = (picture: string): string =>
  /^(https?:)?\//.test(picture) ? picture : `/static/uploads/${picture}`

const mapPdpTag = (tag: PdpProductTag): TileTag => ({
  name: tag.name,
  badgeType: tag.badge_type,
  picture: tag.picture ? uploadsPicture(tag.picture) : null,
})

@customElement('csc-product-tile-tags')
export class CSCProductTileTags extends HiddenFieldsMixin(LitElement, tileTagsHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, partsStyle, tileTagsStyle]

  @consume({ context: productTileProductContext })
  @state()
  product: InternalProduct = new ProductModel().parseToInternal()

  @property({ type: Boolean })
  inline: boolean = false

  @property({ type: String, attribute: 'data-source' })
  dataSource: TileTagsDataSource = 'tile'

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private pdpTags: TileTag[] = []

  constructor() {
    super()
    createEffect(() => {
      const tags = this.store.product()?.tags
      if (tags) this.pdpTags = tags.filter(tag => tag.as_badge || tag.picture).map(mapPdpTag)
    })
  }

  private get tags(): TileTag[] {
    const tags: TileTag[] = this.dataSource === 'pdp-product' ? this.pdpTags : this.product.tags
    return tags.filter(tag => !this.isHidden(tag.picture ? 'pictures' : 'badges'))
  }

  override render() {
    const tags = this.tags
    if (!tags.length) return nothing

    return html`
    <div class="${classMap({ 'badge-group': true, 'badge-group--inline': this.inline })}">
      ${tags.map(tag =>
        tag.picture
          ? html`<cse-image source="${tag.picture}" height="40" alt="${tag.name}"></cse-image>`
          : html`<cse-badge variant=${tag.badgeType ? tag.badgeType : 'default'}><span class="badge-label">${tag.name}</span></cse-badge>`
      )}
    </div>
    `
  }
}
